import { createFileRoute, Link } from "@tanstack/react-router";
import { ArrowRight, Phone } from "lucide-react";
import { SiteHeader } from "@/components/SiteHeader";
import { SiteFooter } from "@/components/SiteFooter";
import { HeroBackground } from "@/components/HeroBackground";
import { Button } from "@/components/ui/button";
import { type ServiceCard } from "@/components/ServiceTile";
import { ServicesCarousel } from "@/components/ServicesCarousel";
import { solarTiles } from "@/data/serviceTiles";
import { company } from "@/data/site";
import { SectionDecor } from "@/components/SectionDecor";
import { PageIntro } from "@/components/PageIntro";
import solarInstallImg from "@/assets/mckay/solar-install.webp.asset.json";
import solarEnergyImg from "@/assets/mckay/solar-energy.webp.asset.json";
import gallery1 from "@/assets/gallery-new/6940e3a8ab25949211304117-2.webp.asset.json";
import gallery2 from "@/assets/gallery-new/6940e3a8b4f420921f7ca893-2.webp.asset.json";
import gallery3 from "@/assets/gallery-new/6940e3a997c0231b045981a5-2.webp.asset.json";

export const Route = createFileRoute("/services/solar")({
  head: () => ({
    meta: [
      { title: "Solar Services in Southern California – McKay Roofing & Solar" },
      { name: "description", content: "Solar installation, battery backup, Tesla Powerwall, inverter service and EV chargers across Southern California. Free estimates." },
      { property: "og:title", content: "Solar Services – McKay Roofing & Solar" },
      { property: "og:description", content: "Solar installation, batteries, and EV charging for homes and businesses in Southern California." },
      { property: "og:type", content: "website" },
    ],
    links: [{ rel: "canonical", href: "https://mckay.steadyautogrowthdemo.com/services/solar" }],
    scripts: [
      {
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "Service",
          serviceType: "Solar Installation",
          name: "Solar Services – McKay Roofing & Solar",
          description: "Solar installation, battery backup, Tesla Powerwall, inverter service and EV chargers across Southern California.",
          provider: {
            "@type": "LocalBusiness",
            name: "McKay Roofing & Solar",
            url: "https://mckay.steadyautogrowthdemo.com",
            telephone: "+1 858-541-1097",
            address: {
              "@type": "PostalAddress",
              addressLocality: "San Diego",
              addressRegion: "CA",
              addressCountry: "US",
            },
          },
          areaServed: { "@type": "AdministrativeArea", name: "Southern California" },
          hasOfferCatalog: {
            "@type": "OfferCatalog",
            name: "Solar Services",
            itemListElement: [
              "Solar Installation","Solar Battery Installation","Commercial Solar",
              "Solar Panel Replacement","Solar Panel Repair","Tesla Powerwall Installation",
              "Solar Inverter Installation","EV Charger Installation",
            ].map((n) => ({ "@type": "Offer", itemOffered: { "@type": "Service", name: n } })),
          },
        }),
      },
    ],
  }),
  component: SolarPage,
});

const solarServices: ServiceCard[] = solarTiles.map((t) => ({ title: t.title, description: t.description, image: t.image, href: t.href }));

function SolarPage() {
  return (
    <div className="flex min-h-screen flex-col">
      <PageIntro storageKey="mckay-loader-solar" variant="solar" />
      <SiteHeader />
      <main className="flex-1">
        {/* Hero */}
        <section className="relative min-h-[85vh] w-full overflow-hidden text-primary-foreground">
          <HeroBackground src={solarInstallImg.url} alt="Solar panel installation on a Southern California home by McKay Roofing & Solar" overlay="black-soft" />
          <div className="relative flex h-full min-h-[85vh] items-center">
            <div className="mx-auto w-full max-w-7xl px-4 py-24 sm:px-6 lg:px-8">
              <div className="max-w-3xl">
                <h1 className="text-4xl font-bold leading-[1.1] sm:text-5xl lg:text-6xl text-balance">
                  Smarter Solar Solutions for Homes and Businesses
                </h1>
                <p className="mt-6 max-w-2xl text-base text-secondary-foreground/85 sm:text-lg">
                  Solar installation, battery backup, panel service and EV charging across Southern California – designed and installed by a team that understands your roof first.
                </p>
                <div className="mt-8 flex flex-wrap items-center gap-4">
                  <Button asChild size="lg" className="bg-primary text-primary-foreground hover:bg-primary/90 shadow-elegant">
                    <Link to="/contact">Get My Free Estimate <ArrowRight className="ml-1 h-4 w-4" /></Link>
                  </Button>
                  <Button asChild size="lg" className="bg-accent text-accent-foreground hover:bg-accent/90 shadow-glow">
                    <Link to="/contact">
                      <Phone className="mr-2 h-4 w-4" />
                      {company.phone}
                    </Link>
                  </Button>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Overview */}
        <section className="relative overflow-hidden bg-background">
          <SectionDecor variant="sun" side="right" tone="primary" opacity={0.05} />
          <div className="relative z-10 mx-auto max-w-4xl px-4 py-16 text-center sm:px-6 sm:py-20 lg:px-8">
            <div className="text-sm font-bold uppercase tracking-wider text-secondary">Solar Done Right</div>
            <h2 className="mt-2 text-3xl font-bold text-primary sm:text-4xl text-balance">
              Solar built around your roof, home, and energy goals
            </h2>
            <p className="mt-6 text-base leading-relaxed text-foreground/85 sm:text-lg">
              McKay handles residential and commercial solar from start to finish – new installs, panel replacements, repairs, battery backup and EV charging. Because we&apos;re a roofing company too, every system is planned so it protects the roof underneath and keeps your home energy-independent for the long haul.
            </p>
          </div>
        </section>

        {/* Services carousel */}
        <ServicesCarousel
          items={solarServices}
          title="Solar Services We Offer"
          subtitle="Full-service solar for Southern California homes and businesses."
        />


        {/* Content sections */}
        <section className="bg-background overflow-x-clip">
          <div className="mx-auto max-w-6xl space-y-20 px-4 py-16 sm:px-6 sm:py-20 lg:px-8">
            <ContentRow
              image={solarEnergyImg.url}
              alt="Home solar panels producing energy"
              side="left"
              eyebrow="When Solar Services Are Needed"
              title="Keep your system producing at its best"
            >
              <p>Solar services are commonly needed when a system experiences reduced energy production, equipment malfunctions, aging components, or when property owners want to upgrade their renewable energy capabilities. Routine inspections and maintenance also help identify potential issues before they impact system performance.</p>
              <p>Whether your goal is to repair an existing system, install battery storage, replace outdated equipment, or expand your solar capacity, timely service helps maximize energy efficiency, lower operating costs, and ensure your system continues delivering reliable performance for years to come.</p>
            </ContentRow>

            <ContentRow
              image={gallery2.url}
              alt="Solar consultation and inspection"
              side="right"
              eyebrow="Our Process"
              title="Simple, clear, and straightforward"
            >
              <p>Our process is designed to be straightforward and easy to understand. It begins with an inspection or consultation to assess the current condition of the roof or solar system. We then explain our findings and outline recommended next steps.</p>
              <p>Once a plan is approved, work is scheduled and completed with clear communication throughout the project. After completion, we review the results and address any remaining questions.</p>
            </ContentRow>

            {/* Middle CTA - solar repair booking */}
            <article className="relative left-1/2 w-screen max-w-[100vw] -translate-x-1/2 bg-primary px-6 py-12 text-primary-foreground sm:px-10">
              <div className="mx-auto flex max-w-3xl flex-col items-center gap-5 text-center">
                <div className="text-sm font-bold uppercase tracking-wider text-primary-foreground/70">Solar Repair Booking</div>
                <h2 className="text-3xl font-bold sm:text-4xl text-balance">
                  System not producing? Book a repair visit online
                </h2>
                <p className="max-w-2xl text-base text-primary-foreground/80">
                  Pick a day and time that works for you and one of our solar technicians will diagnose the issue on site - no phone tag required.
                </p>
                <div className="flex flex-wrap items-center justify-center gap-3">
                  <Button asChild size="lg" className="btn-glow bg-accent text-accent-foreground hover:bg-accent/90 shadow-glow">
                    <Link to="/book/solar-repair">Book a Solar Repair <ArrowRight className="ml-1 h-4 w-4" /></Link>
                  </Button>
                  <Button asChild size="lg" className="bg-white/10 text-primary-foreground hover:bg-white/20">
                    <a href={`tel:${company.phoneHref}`}><Phone className="mr-1 h-4 w-4" /> {company.phone}</a>
                  </Button>
                </div>
              </div>
            </article>


            <ContentRow
              image={gallery3.url}
              alt="Solar installation details"
              side="left"
              eyebrow="What Our Services Include"
              title="Consistent results, clear expectations"
            >
              <p>While each project is different, most roofing and solar services include a similar set of steps to ensure consistent results and clear expectations.</p>
              <ul className="mt-4 space-y-3">
                {[
                  "Inspection and evaluation",
                  "Clear explanation of findings and options",
                  "Repair, replacement, or installation work",
                  "Use of appropriate materials and equipment",
                  "Cleanup and final review",
                ].map((item) => (
                  <li key={item} className="flex items-start gap-3">
                    <span className="mt-2 h-2 w-2 shrink-0 rounded-full bg-secondary" />
                    <span>{item}</span>
                  </li>
                ))}
              </ul>
            </ContentRow>

            <ContentRow
              image={gallery1.url}
              alt="Commercial and residential solar systems"
              side="right"
              eyebrow="Residential and Commercial Properties"
              title="Built for homes and businesses alike"
            >
              <p>Residential and commercial properties have different requirements, and our services are structured to support both. Residential projects often focus on protecting the home and managing long-term maintenance, while commercial projects may involve larger systems and coordinated scheduling.</p>
              <p>Our experience with both property types allows us to adapt our approach based on the scope of work and the needs of the client.</p>
            </ContentRow>
          </div>
        </section>

        {/* CTA */}
        <section className="relative overflow-hidden bg-background">
          <SectionDecor variant="sun" side="left" tone="primary" opacity={0.05} />
          <div className="relative z-10 mx-auto max-w-4xl px-4 py-16 text-center sm:px-6 sm:py-20 lg:px-8">
            <h2 className="text-3xl font-bold text-primary sm:text-4xl text-balance">Ready to Upgrade to Solar?</h2>
            <p className="mx-auto mt-4 max-w-2xl text-muted-foreground">
              Free, no-pressure estimates. We&apos;ll size the right system for your usage and walk you through every number before anything moves forward.
            </p>
            <div className="mt-8 flex flex-wrap items-center justify-center gap-4">
              <Button asChild size="lg" className="bg-primary text-primary-foreground hover:bg-primary/90 shadow-elegant">
                <Link to="/contact">Get My Free Estimate <ArrowRight className="ml-1 h-4 w-4" /></Link>
              </Button>
              <Button asChild size="lg" className="bg-accent text-accent-foreground hover:bg-accent/90 shadow-glow">
                <Link to="/contact">
                  <Phone className="mr-2 h-4 w-4" />
                  {company.phone}
                </Link>
              </Button>
            </div>
          </div>
        </section>
      </main>
      <SiteFooter />
    </div>
  );
}

function ContentRow({
  image,
  alt,
  side,
  eyebrow,
  title,
  children,
}: {
  image: string;
  alt: string;
  side: "left" | "right";
  eyebrow: string;
  title: string;
  children: React.ReactNode;
}) {
  return (
    <div className="grid items-center gap-8 lg:grid-cols-2 lg:gap-12">
      <div className={side === "right" ? "lg:order-2" : ""}>
        <img
          src={image}
          alt={alt}
          loading="lazy"
          className="aspect-[4/3] w-full rounded-2xl object-cover shadow-elegant"
        />
      </div>
      <div>
        <div className="text-sm font-bold uppercase tracking-wider text-secondary">{eyebrow}</div>
        <h2 className="mt-2 text-3xl font-bold text-primary sm:text-4xl text-balance">{title}</h2>
        <div className="mt-6 space-y-4 text-base leading-relaxed text-foreground/85 sm:text-lg">
          {children}
        </div>
      </div>
    </div>
  );
}